import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { routerBasePath } from "@/lib/appPaths";
import { isAuthRoutePath, sanitizeLoginReturnTo, stripQueryAndHash } from "@/lib/authRedirect";
import NotFound from "@/pages/NotFound";
import { Route, Router as WouterRouter, Switch, useLocation } from "wouter";
import { Suspense, lazy, useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CourseProvider } from "./contexts/CourseContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandProvider } from "./contexts/BrandContext";
import { resolveOrganizationDestination } from "./lib/organizationRouting";
import ForgotPassword from "./pages/ForgotPassword";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Signup from "./pages/Signup";

const Home = lazy(() => import("./pages/Home"));
const CourseLibrary = lazy(() => import("./pages/CourseLibrary"));
const ResourceLibrary = lazy(() => import("./pages/ResourceLibrary"));
const TeachingCaseLibrary = lazy(() => import("./pages/TeachingCaseLibrary"));
const CourseHubDetail = lazy(() => import("./pages/CourseHubDetail"));
const AdminCourseReviewPreview = lazy(() => import("./pages/AdminCourseReviewPreview"));
const AdminPayments = lazy(() => import("./pages/AdminPayments"));
const AdminMonitoring = lazy(() => import("./pages/AdminMonitoring"));
const TeacherAdmin = lazy(() => import("./pages/TeacherAdmin"));
const RemotionPreview = lazy(() => import("./pages/RemotionPreview"));
const LotteryPage = lazy(() => import("./pages/LotteryPage"));
const SharePage = lazy(() => import("./pages/SharePage"));
const Layout = lazy(() =>
  import("./components/Layout").then(module => ({ default: module.Layout }))
);

function Router() {
  const [location, setLocation] = useLocation();
  const { status, user } = useAuth();
  const { t } = useLanguage();
  const hasTeachingCaseAccess = Boolean(
    user?.organizations.some(
      item =>
        item.status === "active" &&
        item.organizationCode.trim().toLowerCase() === "sjtu_soe",
    ),
  );
  const routePath = stripQueryAndHash(location);
  const isAuthRoute = isAuthRoutePath(routePath);
  const isShareRoute = routePath.startsWith("/share/");
  const isAdminCourseReviewPreviewRoute = routePath.startsWith("/admin/course-review-preview/");

  useEffect(() => {
    if (status === "authenticated" && isAuthRoute) {
      const query = location.includes("?") ? location.slice(location.indexOf("?") + 1) : "";
      const params = new URLSearchParams(query);
      const returnTo = sanitizeLoginReturnTo(params.get("returnTo"));
      setLocation(resolveOrganizationDestination(returnTo, user?.organization?.code), { replace: true });
      return;
    }
    if (status !== "checking" && !isAuthRoute && !isShareRoute) {
      const destination = resolveOrganizationDestination(location, user?.organization?.code);
      if (destination !== location) {
        setLocation(destination, { replace: true });
      }
    }
  }, [isAuthRoute, isShareRoute, location, setLocation, status, user?.organization?.code]);

  const routes = (
    <Switch>
      <Route path={"/"} component={Home} />
      {hasTeachingCaseAccess ? (
        <Route path={"/education/cases"} component={TeachingCaseLibrary} />
      ) : null}
      <Route path={"/education"} component={Home} />
      <Route path={"/library/:courseId"}>
        {params => <CourseHubDetail courseId={params.courseId} />}
      </Route>
      <Route path={"/library"} component={CourseLibrary} />
      <Route path={"/resources"} component={ResourceLibrary} />
      {user?.isSuperuser ? (
        <Route path={"/admin/monitoring"} component={AdminMonitoring} />
      ) : null}
      {user?.isSuperuser ? (
        <Route path={"/admin/payments"} component={AdminPayments} />
      ) : null}
      {user?.isTeacherAdmin ? (
        <Route path={"/teacher-admin"} component={TeacherAdmin} />
      ) : null}
      <Route path={"/jobs/:jobId/remotion-preview"}>
        {params => <RemotionPreview jobId={params.jobId} />}
      </Route>
      <Route path={"/share/:token"}>
        {params => <SharePage token={params.token} />}
      </Route>
      <Route path={"/lottery"} component={LotteryPage} />
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );

  if (status === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        {t("正在验证登录状态...", "Checking sign-in status...")}
      </div>
    );
  }

  if (isShareRoute) {
    const tokenMatch = routePath.match(/^\/share\/([^/?#]+)/);
    const token = tokenMatch?.[1] || "";
    return (
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
            {t("正在加载分享...", "Loading share...")}
          </div>
        }
      >
        <SharePage token={decodeURIComponent(token)} />
      </Suspense>
    );
  }

  if (isAdminCourseReviewPreviewRoute && user?.isSuperuser) {
    const match = routePath.match(/^\/admin\/course-review-preview\/([^/]+)\/([^/?#]+)/);
    const applicationId = match?.[1] ? decodeURIComponent(match[1]) : "";
    const jobId = match?.[2] ? decodeURIComponent(match[2]) : "";
    return (
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
            {t("正在加载审核预览...", "Loading review preview...")}
          </div>
        }
      >
        {applicationId && jobId ? (
          <AdminCourseReviewPreview applicationId={applicationId} jobId={jobId} />
        ) : (
          <NotFound />
        )}
      </Suspense>
    );
  }

  if (status === "guest") {
    switch (routePath) {
      case "/signup":
        return <Signup />;
      case "/forgot-password":
        return <ForgotPassword />;
      case "/reset-password":
        return <ResetPassword />;
      case "/login":
        return <Login />;
    }
  }

  if (isAuthRoute) {
    return null;
  }

  return (
    <CourseProvider>
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
            {t("正在加载工作台...", "Loading workspace...")}
          </div>
        }
      >
        <Layout>{routes}</Layout>
      </Suspense>
    </CourseProvider>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <Toaster />
            <AuthProvider>
              <WouterRouter base={routerBasePath}>
                <BrandProvider>
                  <Router />
                </BrandProvider>
              </WouterRouter>
            </AuthProvider>
          </TooltipProvider>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
